import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import UserAvatar from '../UserAvatar';
import { useChats } from './ChatRuntimeProvider';
import { PinIcon } from './icons';
import { isPinned } from '../../services/conversations';
import { useSelection } from './SelectionContext';

// Same skin as the old Console: h-24 logo row, project/bot selects, new chat,
// searchable conversation list, nav links, user block. Nothing is wired yet.

export default function Sidebar({ isCollapsed, onToggleCollapse, width = 256 }) {
  const { user, logout } = useAuth();
  const { projects, bots, project, bot, setProject, setBot, isLoading, error, reload } =
    useSelection();
  const {
    chats,
    current,
    isLoading: chatsLoading,
    newChat,
    openChat,
    renameChat,
    removeChat,
    pinChat,
  } = useChats();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [conversationSearch, setConversationSearch] = useState('');
  const userMenuRef = useRef(null);

  // A NAMED window, so clicking again reuses the same one instead of opening a
  // tab per click. focus() matters as much as open(): when the window already
  // exists but is behind, window.open alone looks like nothing happened.
  const openUserPortal = () => {
    setShowUserMenu(false);
    const portal = window.open('/user', 'oplus-user-portal');
    if (portal) portal.focus();
  };

  useEffect(() => {
    if (!showUserMenu) return undefined;

    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserMenu]);

  const pad = isCollapsed ? 'px-2' : 'px-3';

  return (
    <div
      className="bg-white border-r border-gray-200 flex flex-col h-full transition-[width] duration-200 flex-shrink-0"
      style={{ width: isCollapsed ? 64 : width }}
    >
      <div className={isCollapsed ? 'px-2' : 'px-4'}>
        <div className="h-24 min-h-24 flex items-center">
          <button
            onClick={onToggleCollapse}
            className={`flex items-center ${isCollapsed ? 'justify-center' : 'gap-2'} hover:opacity-80 transition-opacity w-full h-full`}
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <div className="w-14 h-14 rounded overflow-hidden flex-shrink-0">
              <img
                src={`${process.env.PUBLIC_URL || ''}/operator_plus_logo_hd.png`}
                alt="Operator+"
                className="w-full h-full object-contain"
              />
            </div>
            {!isCollapsed && <h1 className="text-2xl font-semibold text-gray-900">Operator+</h1>}
          </button>
        </div>

        {!isCollapsed && (
          <div className="mt-3 pb-3 space-y-3">
            <Selector
              label="Project"
              value={project}
              onChange={setProject}
              options={projects}
              isLoading={isLoading}
              placeholder="Select a project"
              emptyLabel="No projects available"
            />
            <Selector
              label="Bot"
              value={bot}
              onChange={setBot}
              options={bots}
              placeholder="Select a bot"
              emptyLabel={project ? 'No bots available' : 'Select a project first'}
              disabled={!project}
            />

            {error && (
              <div className="px-3">
                <p className="text-xs text-red-600">
                  {error}{' '}
                  <button onClick={reload} className="underline hover:text-red-700">
                    Retry
                  </button>
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      <div className={`${pad} py-2`}>
        <button
          onClick={newChat}
          className={`w-full flex items-center ${isCollapsed ? 'justify-center' : 'gap-2'} px-3 py-2 text-base font-medium text-gray-800 bg-gray-50 hover:bg-gray-100 rounded-md transition-colors`}
          title="New chat"
        >
          <div className="w-4 h-4 bg-primary-600 rounded-full flex items-center justify-center flex-shrink-0">
            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </div>
          {!isCollapsed && <span>New chat</span>}
        </button>
      </div>

      {/* min-h-0 is what makes this scroll: a flex child defaults to
          min-height:auto, so without it the list grows and pushes the nav and
          the user block off the bottom instead of scrolling */}
      <div className={`flex-1 min-h-0 flex flex-col ${pad}`}>
        {!isCollapsed && (
          <>
            {/* the heading and search stay put; only the list scrolls */}
            <div className="px-3 py-2 flex-shrink-0">
              <h3 className="text-sm font-semibold tracking-wide text-gray-600 uppercase">
                Recent conversations
              </h3>
              <div className="mt-2 relative">
                <input
                  type="text"
                  value={conversationSearch}
                  onChange={(e) => setConversationSearch(e.target.value)}
                  placeholder="Search conversations..."
                  className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent placeholder-gray-500"
                  aria-label="Search recent conversations"
                />
                <svg
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto pb-2">
              <ChatList
                chats={chats}
                current={current}
                search={conversationSearch}
                isLoading={chatsLoading}
                onOpen={openChat}
                onRename={renameChat}
                onDelete={removeChat}
                onPin={pinChat}
              />
            </div>
          </>
        )}
      </div>

      <div className={`${pad} py-2 border-t border-gray-200`}>
        <div className="space-y-1">
          <NavItem isCollapsed={isCollapsed} label="Documentation">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </NavItem>
          <NavItem isCollapsed={isCollapsed} label="Support">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M12 2.25a9.75 9.75 0 100 19.5 9.75 9.75 0 000-19.5z" />
          </NavItem>
        </div>
      </div>

      <div className={`${pad} py-3 relative`} ref={userMenuRef}>
        {isCollapsed ? (
          <button
            onClick={logout}
            className="w-full flex items-center justify-center p-2 rounded-md hover:bg-gray-50 transition-colors"
            title="Logout"
          >
            <UserAvatar user={user} size="sm" />
          </button>
        ) : (
          <>
            <button
              onClick={() => setShowUserMenu((open) => !open)}
              className="w-full flex items-center gap-2 p-2 rounded-md hover:bg-gray-50 transition-colors"
            >
              <UserAvatar user={user} size="sm" />
              <div className="flex-1 text-left min-w-0">
                <div className="text-base font-medium text-gray-900 truncate">{user?.name || 'User'}</div>
                <div className="text-xs text-gray-500 truncate">{user?.email || 'user@example.com'}</div>
              </div>
              <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {showUserMenu && (
              <div className="absolute bottom-14 left-3 right-3 bg-white rounded-md shadow-lg border border-gray-200 z-10">
                <button
                  type="button"
                  onClick={openUserPortal}
                  title="Manage your own connected accounts"
                  className="w-full text-left p-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors rounded-md flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span>My Account</span>
                </button>

                <div className="border-t border-gray-100" />

                <button
                  type="button"
                  onClick={logout}
                  className="w-full text-left p-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors rounded-md flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  <span>Logout</span>
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// The chat history. Titles come from metadata, which the backend fills in from
// the first message and then replaces with the LLM's version.
function ChatList({ chats, current, search, isLoading, onOpen, onRename, onDelete, onPin }) {
  const [renaming, setRenaming] = useState(null);
  const [confirming, setConfirming] = useState(null);
  const query = search.trim().toLowerCase();
  const visible = query
    ? chats.filter((chat) => titleOf(chat).toLowerCase().includes(query))
    : chats;

  if (isLoading && chats.length === 0) {
    return (
      <div className="space-y-1">
        {[1, 2, 3].map((i) => (
          <div key={i} className="w-full px-3 py-2 rounded-md bg-gray-100 animate-pulse">
            <div className="h-3 bg-gray-200 rounded w-3/4" />
          </div>
        ))}
      </div>
    );
  }

  if (visible.length === 0) {
    return (
      <div className="px-3 py-6 text-sm text-gray-500">
        {query ? 'No conversations match your search.' : 'No conversations yet.'}
      </div>
    );
  }

  // the pinned ones are already first from the server; the split is only so
  // they get a heading and don't read as "most recent"
  const pinned = visible.filter(isPinned);
  const rest = visible.filter((chat) => !isPinned(chat));

  const row = (chat) => {
    if (confirming === chat.id) {
      return (
        <ConfirmDeleteRow
          key={chat.id}
          chat={chat}
          onDone={(confirmed) => {
            setConfirming(null);
            if (confirmed) onDelete(chat);
          }}
        />
      );
    }

    if (renaming === chat.id) {
      return (
        <RenameRow
          key={chat.id}
          chat={chat}
          onDone={(title) => {
            if (title !== null) onRename(chat, title);
            setRenaming(null);
          }}
        />
      );
    }

    return (
      <div key={chat.id} className="relative group">
        <button
          onClick={() => onOpen(chat)}
          title={`${titleOf(chat)}\n${sourceOf(chat)}`}
          // pr-9 clears the pin, which is the only one showing at rest. The
          // other two appear on hover, so the room for them has to appear too:
          // without this the title kept its full width and ran underneath
          // them, in a weight and colour close enough to be unreadable.
          className={`w-full flex items-start gap-2 px-3 py-2 pr-9 text-left rounded-md transition-colors group-hover:pr-24 group-focus-within:pr-24 ${
            current === chat.id ? 'bg-primary-50 text-primary-700' : 'text-gray-800 hover:bg-gray-50'
          }`}
        >
          <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>

          {/* min-w-0 is what makes truncate work at all: without it a flex
              child keeps its intrinsic width and overflows instead */}
          <span className="min-w-0 flex-1">
            <span className="block truncate text-base font-medium">{titleOf(chat)}</span>
            <span
              className={`block truncate text-xs ${
                current === chat.id ? 'text-primary-600' : 'text-gray-500'
              }`}
            >
              {sourceOf(chat)}
            </span>
          </span>
        </button>

        {/* A pinned chat shows its pin at rest so the state is visible without
            hovering; rename and delete stay hidden until you do. `hidden`
            rather than opacity-0, or the invisible buttons would still take up
            room and push the lone pin out over the title. */}
        <div
          className={`absolute right-1.5 top-1/2 flex -translate-y-1/2 gap-0.5 rounded-md ${
            current === chat.id ? 'bg-primary-50' : 'bg-white group-hover:bg-gray-50'
          }`}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              onPin(chat, !isPinned(chat));
            }}
            title={isPinned(chat) ? 'Unpin chat' : 'Pin chat'}
            aria-label={`${isPinned(chat) ? 'Unpin' : 'Pin'} ${titleOf(chat)}`}
            className={`rounded p-1 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-700 group-focus-within:block group-hover:block ${
              isPinned(chat) ? 'block' : 'hidden'
            }`}
          >
            {/* A pinned chat keeps its accent; an unpinned pin inherits the
                button, so it darkens on hover with the other two. */}
            <PinIcon
              className={`w-4 h-4 ${isPinned(chat) ? 'text-primary-600' : ''}`}
              filled={isPinned(chat)}
            />
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              setRenaming(chat.id);
            }}
            title="Rename chat"
            aria-label={`Rename ${titleOf(chat)}`}
            className="hidden rounded p-1 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-700 group-focus-within:block group-hover:block"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
            </svg>
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              setConfirming(chat.id);
            }}
            title="Delete chat"
            aria-label={`Delete ${titleOf(chat)}`}
            className="hidden rounded p-1 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-700 group-focus-within:block group-hover:block"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 7h12M9 7V5h6v2m-7 0 1 12h6l1-12" />
            </svg>
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-1">
      {pinned.length > 0 && <GroupLabel>Pinned</GroupLabel>}
      {pinned.map(row)}

      {pinned.length > 0 && rest.length > 0 && <GroupLabel>Recent</GroupLabel>}
      {rest.map(row)}
    </div>
  );
}

function GroupLabel({ children }) {
  return (
    <div className="px-3 pt-2 pb-1 text-xs font-medium uppercase tracking-wide text-gray-400">
      {children}
    </div>
  );
}

// Renaming in place rather than in a modal: the title is three words and the
// row it belongs to is the only context needed.
function RenameRow({ chat, onDone }) {
  const [value, setValue] = useState(titleOf(chat));

  return (
    <input
      autoFocus
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={() => onDone(value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onDone(value);
        if (e.key === 'Escape') onDone(null);
      }}
      className="w-full rounded-md border border-primary-500 px-3 py-2 text-base text-gray-900 outline-none ring-2 ring-inset ring-primary-500"
      aria-label="Chat title"
    />
  );
}

// Same in-place treatment as the rename: a chat is gone for good once deleted,
// so it needs a confirm, but a modal for one row is more ceremony than it's
// worth. Autofocus lands on Cancel so a stray Enter can't delete anything.
function ConfirmDeleteRow({ chat, onDone }) {
  return (
    <div
      className="rounded-md border border-red-300 bg-red-50 px-3 py-2"
      onKeyDown={(e) => {
        if (e.key === 'Escape') onDone(false);
      }}
    >
      <p className="truncate text-sm text-gray-800">
        Delete <span className="font-medium">{titleOf(chat)}</span>?
      </p>
      <div className="mt-2 flex gap-2">
        <button
          autoFocus
          onClick={() => onDone(false)}
          className="flex-1 rounded border border-gray-300 bg-white px-2 py-1 text-sm text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          onClick={() => onDone(true)}
          className="flex-1 rounded bg-red-600 px-2 py-1 text-sm font-medium text-white hover:bg-red-700"
        >
          Delete
        </button>
      </div>
    </div>
  );
}

// project and bot ride in metadata, so a chat knows which bot it belongs to
// even though the sidebar lists them all. Showing it matters here: the list is
// deliberately not filtered by the current selection, so without it you can't
// tell why an old chat answers differently.
const titleOf = (chat) => chat.metadata?.title || 'Untitled conversation';

const sourceOf = (chat) => {
  const project = chat.metadata?.project;
  const bot = chat.metadata?.bot;

  if (!project && !bot) return 'unknown bot';

  return [project, bot].filter(Boolean).join(' / ');
};

function Selector({
  label,
  value,
  onChange,
  options,
  placeholder,
  emptyLabel,
  isLoading = false,
  disabled = false,
}) {
  const isEmpty = !isLoading && options.length === 0;
  const head = isLoading ? 'Loading...' : isEmpty ? emptyLabel : placeholder;

  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1.5 px-3">{label}</label>
      <div className="px-3">
        <select
          value={value}
          disabled={disabled || isLoading || isEmpty}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-400"
          aria-label={label}
        >
          <option value="">{head}</option>
          {options.map((option) => (
            <option key={option.name} value={option.name}>
              {option.title || option.name}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

function NavItem({ isCollapsed, label, children }) {
  return (
    <button
      className={`w-full flex items-center ${isCollapsed ? 'justify-center' : 'gap-2'} px-3 py-2 text-base font-medium text-gray-800 hover:bg-gray-50 rounded-md transition-colors`}
      title={label}
    >
      <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        {children}
      </svg>
      {!isCollapsed && <span>{label}</span>}
    </button>
  );
}
